import PropTypes from 'prop-types'
import moment from 'moment'
import { useTranslation } from 'react-i18next'

import Drawer from '../common/Drawer/Drawer'
import DrawerAvatar from '../common/Drawer/DrawerAvatar'
import DrawerFacts from '../common/Drawer/DrawerFacts'
import DrawerLink from '../common/Drawer/DrawerLink'
import DrawerSection from '../common/Drawer/DrawerSection'
import DrawerCard from '../common/Drawer/DrawerCard'
import DrawerEmptyState from '../common/Drawer/DrawerEmptyState'
import CopyableValue from '../common/buttons/CopyableValue'
import CoachStatus from './CoachStatus'
import CoachCertification from './CoachCertification'
import CoachDrawerOrganizations from './CoachDrawerOrganizations'
import CoachDrawerAccolades from './CoachDrawerAccolades'
import CoachDrawerActions from './CoachDrawerActions'
import { formatPhoneForLinks } from '../../utility/phoneHelper'
import { initialsOf } from '../../utility/helperFunctions'

import AvatarPlaceholder from '../../assets/images/mock/default-profile-icon.png'

const formatDate = (date, fallback) => (date ? moment(date).format('MMM D, YYYY') : fallback)

const CoachDrawer = ({ coach, onClose }) => {
  const { t } = useTranslation()
  const community = coach.communities?.[0]
  const fullName = `${coach.first_name || ''} ${coach.last_name || ''}`.trim()

  const facts = [
    { label: t('coaches.drawer.coachId'), value: <CopyableValue value={coach.id} label={t('coaches.drawer.coachId')} /> },
    {
      label: t('coaches.table.idpId'),
      value: coach.oauth_external_id
        ? <CopyableValue value={coach.oauth_external_id} label={t('coaches.table.idpId')} />
        : t('coaches.table.notAvailable'),
    },
    { label: t('common.field.status'), value: <CoachStatus status={coach.status} /> },
    { label: t('coaches.table.certification'), value: <CoachCertification coach={coach} /> },
    { label: t('coaches.table.lastSignIn'), value: formatDate(coach.current_sign_in_at, t('coaches.table.never')) },
    { label: t('coaches.table.dateCreated'), value: formatDate(coach.created_at, t('coaches.table.notAvailable')) },
    {
      label: t('common.field.phoneNumber'),
      value: coach.phone
        ? <a href={`tel:${formatPhoneForLinks(coach.phone_prefix, coach.phone)}`}>{coach.phone_prefix}{coach.phone}</a>
        : t('coaches.table.notAvailable'),
    },
  ]

  return (
    <Drawer
      onClose={onClose}
      avatar={<DrawerAvatar src={coach.avatar?.file_url || AvatarPlaceholder} alt={fullName} />}
      title={fullName}
      subtitle={<CopyableValue value={coach.email} label={t('common.field.email')} />}
    >
      <DrawerFacts facts={facts} />

      <DrawerLink to={`/profile/${coach.id}`} icon='fa-user' external>{t('common.action.viewProfile')}</DrawerLink>

      <DrawerSection title={t('coaches.common.community')} hint={t('coaches.drawer.communityHint')}>
        {community ? (
          <DrawerCard
            icon={initialsOf(community.name)}
            image={community.photo?.small_file_url}
            iconVariant='avatar'
            label={community.name}
            to={`/communities/${community.id}`}
          />
        ) : (
          <DrawerEmptyState>{t('coaches.drawer.noCommunity')}</DrawerEmptyState>
        )}
      </DrawerSection>

      <CoachDrawerOrganizations coach={coach} />
      <CoachDrawerAccolades coach={coach} />
      <CoachDrawerActions coach={coach} />
    </Drawer>
  )
}

CoachDrawer.propTypes = {
  coach: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
}

export default CoachDrawer
